/**
 * useEnumOptions - 枚举选项 React Hook
 *
 * 提供便捷的枚举选项获取和使用方法，支持自动缓存和状态管理。
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getEnumOptions,
  EnumOption,
} from '../services/enumConfig';

// 重新导出 EnumOption 类型供外部使用
export type { EnumOption };

/**
 * 获取枚举选项列表
 */
export function useEnumOptions(categoryCode: string): {
  options: EnumOption[];
  loading: boolean;
  error: Error | null;
  refresh: (force?: boolean) => void;
} {
  const [options, setOptions] = useState<EnumOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOptions = useCallback(async (force: boolean = false) => {
    if (!categoryCode) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getEnumOptions(categoryCode, force);
      setOptions(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('获取枚举选项失败'));
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [categoryCode]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  return {
    options,
    loading,
    error,
    refresh: fetchOptions,
  };
}

/**
 * 根据值获取标签
 */
export function useEnumLabel(categoryCode: string, value: string | undefined): string {
  const { options } = useEnumOptions(categoryCode);

  return useMemo(() => {
    if (!value) return '';
    const option = options.find(o => o.value === value);
    return option?.label || value;
  }, [options, value]);
}

/**
 * 根据值获取颜色
 */
export function useEnumColor(categoryCode: string, value: string | undefined): string {
  const { options } = useEnumOptions(categoryCode);

  return useMemo(() => {
    if (!value) return 'default';
    const option = options.find(o => o.value === value);
    return option?.color || 'default';
  }, [options, value]);
}

/**
 * 获取枚举选项的标签映射
 */
export function useEnumLabelMap(categoryCode: string): Record<string, string> {
  const { options } = useEnumOptions(categoryCode);

  return useMemo(() => {
    const map: Record<string, string> = {};
    options.forEach(opt => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [options]);
}

/**
 * 获取枚举选项的颜色映射
 */
export function useEnumColorMap(categoryCode: string): Record<string, string> {
  const { options } = useEnumOptions(categoryCode);

  return useMemo(() => {
    const map: Record<string, string> = {};
    options.forEach(opt => {
      if (opt.color) {
        map[opt.value] = opt.color;
      }
    });
    return map;
  }, [options]);
}

/**
 * 转换为 Ant Design Select options 格式
 */
export function useSelectOptions(categoryCode: string): Array<{ value: string; label: string }> {
  const { options } = useEnumOptions(categoryCode);

  return useMemo(() => {
    return options.map(opt => ({
      value: opt.value,
      label: opt.label,
    }));
  }, [options]);
}

/**
 * 获取完整的选项信息（标签+颜色）
 */
export function useEnumOptionInfo(
  categoryCode: string,
  value: string | undefined
): { label: string; color: string; description?: string } {
  const { options } = useEnumOptions(categoryCode);

  return useMemo(() => {
    if (!value) return { label: '', color: 'default' };
    const option = options.find(o => o.value === value);
    return {
      label: option?.label || value,
      color: option?.color || 'default',
      description: option?.description,
    };
  }, [options, value]);
}

/**
 * 批量获取多个枚举分类的选项
 */
export function useMultipleEnumOptions(categoryCodes: string[]): {
  optionsMap: Record<string, EnumOption[]>;
  loading: boolean;
  error: Error | null;
} {
  const [optionsMap, setOptionsMap] = useState<Record<string, EnumOption[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!categoryCodes.length) {
      setOptionsMap({});
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    Promise.all(categoryCodes.map(code => getEnumOptions(code)))
      .then(results => {
        const map: Record<string, EnumOption[]> = {};
        categoryCodes.forEach((code, index) => {
          map[code] = results[index];
        });
        setOptionsMap(map);
      })
      .catch(err => {
        setError(err instanceof Error ? err : new Error('获取枚举选项失败'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [categoryCodes.join(',')]);

  return { optionsMap, loading, error };
}